/**
 * Created by Rodrigo Quesada on 16/03/17.
 */
import {forwardRef, Inject, Injectable} from '@angular/core';
import {User} from './user.model';
import {BehaviorSubject, Observable, ReplaySubject} from 'rxjs';
import {AuthService} from '../../../components/security/authorization/auth.service';
import {LocalStorageService} from 'angular-2-local-storage';
import {Principal} from '../../../components/security/authorization/principal.class';
import {NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router} from '@angular/router';

//region Other Classes
interface UserImpersonationData {
    userId: string;
    impersonatedUserId?: string;
    impersonatedUserRemoteId?: string;
    impersonatedUserFullName?: string;
}

//endregion

const STORAGE_KEY = 'userImpersonationService';
const IMPERSONATEABLE_PAGE_DATA = 'impersonationAllowed';
const IMPERSONATEABLE_WITH_REMOTE_ID_DATA = 'impersonateRemoteId';

@Injectable()
export class UserImpersonationService {

    //region impersonatedUserId
    private _impersonatedUserValue?: UserImpersonationData;
    private _impersonatedUserSubject = new BehaviorSubject<UserImpersonationData|undefined>(undefined);


    private get _impersonatedUser(): UserImpersonationData|undefined { return this._impersonatedUserValue; }

    private set _impersonatedUser(value: UserImpersonationData|undefined) {
        this._impersonatedUserValue = value;
        this._impersonatedUserSubject.next(value);
    }

    get impersonatedUserId(): string|undefined { return this._impersonatedUser ? this._impersonatedUser.impersonatedUserId : undefined; }
    get impersonatedUserRemoteId(): string|undefined { return this._impersonatedUser ? this._impersonatedUser.impersonatedUserRemoteId : undefined; }
    get impersonatedUserFullName(): string|undefined { return this._impersonatedUser ? this._impersonatedUser.impersonatedUserFullName : undefined; }

    private _dataRefreshedSubject = new ReplaySubject<boolean|undefined>(1);
    readonly dataRefreshed$: Observable<boolean> = this._dataRefreshedSubject.filter(it => it !== undefined);

    //endregion

    get impersonatedUserChange$(): Observable<boolean> { return this._impersonatedUserSubject.map(it => !!it || (it !== undefined) ? !!it.impersonatedUserId : false); }

    isImpersonationAllowedOnPage: boolean = false;
    isImpersonatingWithRemoteId: boolean = false;

    constructor(@Inject(forwardRef(() => AuthService)) private readonly _authService: AuthService,
                private readonly _storageService: LocalStorageService,
                private readonly _router: Router) {

        this._authService.user$.subscribe(user => {
            if (user) this._loadData(user);
            else this._clearData();
        });

        this._impersonatedUserSubject.skip(1).subscribe(value => {
            const data = this._storageService.get<UserImpersonationData|undefined>(STORAGE_KEY);
            if (data) {
                data.impersonatedUserId = value ? value.impersonatedUserId : undefined;
                data.impersonatedUserRemoteId = value ? value.impersonatedUserRemoteId : undefined;
                data.impersonatedUserFullName = value ? value.impersonatedUserFullName : undefined;
                this._storageService.set(STORAGE_KEY, data);
            }
        });

        this._router.events
            .subscribe(event => {

                if (event instanceof NavigationStart) this._dataRefreshedSubject.next(undefined);
                else if (event instanceof NavigationEnd) {

                    let impersonationAllowed = false;

                    let node = this._router.routerState.root;
                    while (node) {
                        const data = node.snapshot.data;
                        if (data) {
                            const value = data[IMPERSONATEABLE_PAGE_DATA];
                            if (value !== undefined) impersonationAllowed = value;
                        }

                        node = node.firstChild;
                    }

                    this.isImpersonationAllowedOnPage = impersonationAllowed;
                    if (impersonationAllowed) {
                        let impersonateWithRemoteId = false;
                        node = this._router.routerState.root;
                        while (node) {
                            const data = node.snapshot.data;
                            if (data) {
                                const value = data[IMPERSONATEABLE_WITH_REMOTE_ID_DATA];
                                if (value !== undefined) impersonateWithRemoteId = value;
                            }

                            node = node.firstChild;
                        }
                        this.isImpersonatingWithRemoteId = impersonateWithRemoteId;
                    }
                    this._dataRefreshedSubject.next(true);
                }
                else if (event instanceof NavigationCancel || event instanceof NavigationError) {
                    this._dataRefreshedSubject.next(true);
                }
            });
    }

    private _loadData(user: Principal) {
        let data: UserImpersonationData|undefined = this._storageService.get<UserImpersonationData|undefined>(STORAGE_KEY);

        if (!data || data.userId !== user.id) {
            //user-scoped
            data = {userId: user.id};
            this._storageService.set(STORAGE_KEY, data);
        }

        this._impersonatedUser = data;
    }

    private _clearData() {
        this._storageService.remove(STORAGE_KEY);
        this._impersonatedUser = undefined;
    }

    impersonateUser(user: User, navigateTo: any[]) { this._changeImpersonatingUserIdAndNavigate(user, navigateTo); }

    stopImpersonatingUser(navigateTo: any[]) { this._changeImpersonatingUserIdAndNavigate(undefined, navigateTo); }

    private _changeImpersonatingUserIdAndNavigate(user: User|undefined, navigateTo: any[]): void {
        this._dataRefreshedSubject.next(undefined);
        if (user)  {
            this._impersonatedUser = {
                userId : '',
                impersonatedUserId : user.id,
                impersonatedUserRemoteId : user.remoteId,
                impersonatedUserFullName: `${user.name.firstName}${user.name.middleName ? ` ${user.name.middleName}` : ''} ${user.name.lastName}`,
            };
        } else {
            this._impersonatedUser = undefined;
        }
        this._router.navigate(navigateTo);
    }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./~/tslint-loader!./src/app/domain/model/user/user-impersonation.service.ts